import React from 'react'
import PropTypes from 'prop-types'
import { Button, IconButton } from '@material-ui/core'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { connect } from 'react-redux'
import classnames from 'classnames'
import linkTypes from '../enums/linkTypes'
import stepTypes from '../enums/stepTypes'
import stepStatusTypes from '../enums/stepStatusTypes'
import pick from '../utilities/pick'
import getField from '../utilities/Data/getField'
import { statisticNameFor, statisticValueFor, genderDisplayFor } from '../utilities/Data/User/calculateStatistics'
import getProfileStatus from '../utilities/Data/User/getProfileStatus'
import getValueInUnit from '../utilities/Data/User/getValueInUnit'
import ImproveBox from '../components/ImproveBox'
import mapDispatchers from '../utilities/mapDispatchers'
import ExpandableSection from '../components/ExpandableSection'
import Collection from '../components/Collection'
import Img from '../components/Img'
import ContextMenu from '../components/ContextMenu'
import AddButton from '../components/AddButton'
import UserImproveBox from '../components/User/UserImproveBox'
import getShortName from '../utilities/Data/User/getShortName'
import getUserProfileLocation from '../utilities/Data/User/getUserProfileLocation'
import UserSchema from '../utilities/Data/User'
import styles from './User.icss'
import getStartLocationForUser from '../utilities/getStartLocationForUser'

//	See if we should show the stat link
const hasNoStatValue = (stats, attrib) => (!stats || !stats[attrib] || !stats[attrib].value || !getValueInUnit(stats[attrib]))

class User extends React.Component {
	constructor(props) {
		super(props)

		this.state = {}

		this.elementRefs = {}
		this.addRef = refId => (element) => {
			this.elementRefs[refId] = element
		}

		this.handleEditStat = this.handleEditStat.bind(this)
		this.handleMakePrivateStat = this.handleMakePrivateStat.bind(this)
		this.handleMakePublicStat = this.handleMakePublicStat.bind(this)
		this.handleContextOpenForUserInfo = this.handleContextOpenForUserInfo.bind(this)
		this.handleContextCloseForUserInfo = this.handleContextCloseForUserInfo.bind(this)
		this.handleEditUserInfo = this.handleEditUserInfo.bind(this)
		this.renderFullNameContextMenu = this.renderFullNameContextMenu.bind(this)
		this.bindScrollToElement = this.bindScrollToElement.bind(this)
		this.typeStatus = this.typeStatus.bind(this)
		this.getIconForType = this.getIconForType.bind(this)
		this.renderVerificationMessage = this.renderVerificationMessage.bind(this)
	}

	static getDerivedStateFromProps(props) {
		const userName = props && props.match && props.match.params ?
			props.match.params.userName :
			null

		return { userName }
	}

	componentDidMount() {
		if (!UserSchema.isOnboardingComplete(this.props.user)) {
			this.props.appRedirect(getStartLocationForUser(this.props.user))
		} else {
			this.props.appBottomBarValue(linkTypes.USER)
			this.props.appBackButtonFunction(false)
			// only showing user's own profile when not activated
			this.props.userProfileGetOwnProfile()
			if (this.state.userName !== getField(this.props.user, 'userProfile.userName')) {
				// if the location was for a different user, redirect
				this.props.appRedirect(getUserProfileLocation(this.props.user))
			}
		}
	}

	componentDidUpdate() {
		if (!UserSchema.isOnboardingComplete(this.props.user)) {
			this.props.appRedirect(getStartLocationForUser(this.props.user))
		}
	}

	getIconForType(type) {
		const icons = {
			[stepStatusTypes.COMPLETED]: '/images/content/badges/full.svg',
			[stepStatusTypes.IN_PROGRESS]: '/images/content/badges/half.svg',
			[stepStatusTypes.INCOMPLETE]: '/images/content/badges/incomplete.svg'
		}
		const myStatus = this.typeStatus(type)
		return icons[myStatus.status]
	}

	typeStatus(type) {
		return getProfileStatus(this.props.userProfile, type)
	}

	handleEditStat(stat, statType) {
		const defaultLink = 'statistics'
		const linkableTypes = {
			weight: 'weight',
			waist: 'waist',
			height: 'height',
			chest: 'chest',
			chestShape: 'shape',
			fitPreference: 'shape',
			stomachShape: 'shape'
		}
		const linkPath = linkableTypes[statType] ? linkableTypes[statType] : defaultLink
		this.props.appRedirect(`/edit${linkPath}`)
	}

	handleMakePrivateStat(stat) {
		console.log('handleMakePrivateStat', this.state.userName, stat)
	}

	handleMakePublicStat(stat) {
		console.log('handleMakePublicStat', this.state.userName, stat)
	}

	handleContextCloseForUserInfo() {
		const showFullNameContextMenu = null
		this.setState({ showFullNameContextMenu })
	}

	handleContextOpenForUserInfo() {
		const showFullNameContextMenu = true
		this.setState({ showFullNameContextMenu })
	}

	handleEditUserInfo() {
		return () => {
			this.props.appRedirect('/editinfo')
		}
	}

	bindScrollToElement(refId) {
		return () => this.elementRefs[refId].scrollIntoView({ block: 'start', behavior: 'smooth' })
	}

	bindScrollToElementOrLinkIfIncomplete(refId, status, link) {
		if (status === stepStatusTypes.INCOMPLETE) {
			return () => {
				this.props.appRedirect(link)
			}
		}
		return this.bindScrollToElement(refId)
	}

	renderFullNameContextMenu() {
		const { showFullNameContextMenu } = this.state
		const anchorEl = this.elementRefs.editUserFullName

		if (showFullNameContextMenu && anchorEl) {
			return (
				<ContextMenu
					contextMenuItems={[{
						name: this.context.translate('Edit'),
						handler: this.handleEditUserInfo
					}]}
					handleContextClose={this.handleContextCloseForUserInfo}
					anchorEl={anchorEl}
					menuItemClickBinder={this.handleEditUserInfo}
					item={showFullNameContextMenu}
				/>
			)
		}
		return null
	}

	renderVerificationMessage() {
		if (this.props.verifiedUserObject && this.props.verifiedUserObject.realName) {
			return this.props.verifiedUserObject.alreadyVerified ?
				this.context.translate('You have already verified {verifiedUser}.', { verifiedUser: this.props.verifiedUserObject.realName }) :
				this.context.translate('You just verified {verifiedUser}!', { verifiedUser: this.props.verifiedUserObject.realName })
		}
		return undefined
	}

	render() {
		const up = this.props.userProfile

		if (up) {
			up.verifications = up.verifications || []
		}

		return (
			<div className={styles.wrapper}>
				{up && (
					<div>
						<section className={styles.pagePadding}>
							<h2>{this.context.translate('Hi {name},', { name: getShortName(this.props) })}</h2>
							<p>
								{this.renderVerificationMessage()}
							</p>
							<p>
								{this.context.translate('Welcome to the queue for our closed Beta!')}
							</p>
							<p>
								{this.context.translate('Complete the details below in as much detail as you can to propel yourself up the queue.')}
							</p>
							<p>
								{this.context.translate('Complete and accurate profiles will automatically be promoted and have the chance to become a Kitfit Ambassador.')}
							</p>
							<p>
								{this.context.translate('Everyone else will be onboarded in order of profile completedness!')}
							</p>
						</section>

						<section className={classnames(styles.pagePadding, styles.buttonBoxSurround)}>
							<div className={styles.buttonBox}>
								<div className={styles.buttonSurround}>
									<Button variant="contained" className={this.typeStatus(stepTypes.BASICINFO).completed ? styles.buttonStepCompleted : styles.buttonStepIncomplete} onClick={() => this.props.appRedirect('/editinfo')}>
										{this.context.translate('Basic info')}
									</Button>
									<Img content={this.getIconForType(stepTypes.BASICINFO)} />
								</div>
								<div className={styles.buttonSurround}>
									<Button variant="contained" className={this.typeStatus(stepTypes.YOURFIT).completed ? styles.buttonStepCompleted : styles.buttonStepIncomplete} onClick={this.bindScrollToElement(stepTypes.YOURFIT)}>
										{this.context.translate('Your stats')}
									</Button>
									<Img content={this.getIconForType(stepTypes.YOURFIT)} />
								</div>
								<div className={styles.buttonSurround}>
									<Button variant="contained" className={this.typeStatus(stepTypes.VERIFICATIONS).completed ? styles.buttonStepCompleted : styles.buttonStepIncomplete} onClick={() => this.props.appRedirect('/addVerification')}>
										{this.typeStatus(stepTypes.VERIFICATIONS).status === stepStatusTypes.INCOMPLETE ? this.context.translate('Get verified') : this.context.translate('Get more verifications')}
									</Button>
									<Img content={this.getIconForType(stepTypes.VERIFICATIONS)} />
									{up.verifications && up.verifications.length > 0 && (
										<span className={styles.verifiedIcon}>
											<Img content="/images/content/badges/verified.svg" />
											<span className={styles.verifiedIconText}>{up.verifications.length}</span>
										</span>
									)}
								</div>
							</div>
						</section>

						<span ref={this.addRef(stepTypes.BASICINFO)} />

						<section className={styles.pagePadding}>
							<div className={styles.userFullName}>
								<h3 className={styles.fullNameHeader}>{up.fullName}</h3>
								<IconButton className={styles.kebobMenu} aria-label="Context" onClick={this.handleContextOpenForUserInfo}>
									<span ref={this.addRef('editUserFullName')} />

									<MoreVertIcon />
								</IconButton>
								{this.renderFullNameContextMenu()}
							</div>
							{up.instagramHandle && (
								<div className={styles.basicInfoSurround}>
									<span>{this.context.translate('Instagram: ')}</span>
									<a target="_blank" rel="noopener noreferrer" href={`https://www.instagram.com/${up.instagramHandle}`}>{`@${up.instagramHandle}`}</a>
								</div>
							)}
							{up.bio && (
								<div className={styles.basicInfoSurround}>
									{up.bio}
								</div>
							)}
						</section>

						{(!up.bio || !up.instagramHandle) && (
							<ImproveBox text={this.context.translate('Improve your basic info! Add this:')}>
								{!up.bio && (
									<div className={styles.buttonLine}>
										<AddButton onClick={() => this.props.appRedirect('/editinfo')} className={styles.addButton}>{this.context.translate('Add a Bio')}</AddButton>
									</div>
								)}
								{!up.instagramHandle && (
									<div className={styles.buttonLine}>
										<AddButton onClick={() => this.props.appRedirect('/editinfo')} className={styles.addButton}>{this.context.translate('Add your Instagram handle')}</AddButton>
									</div>
								)}
							</ImproveBox>
						)}

						<ExpandableSection title={up.verifications ? this.context.translate('{verifications|# - Verification|# - Verifications}', { verifications: up.verifications.length }) : this.context.translate('Verifications')} defaultExpanded>
							<Collection
								items={this.props.userProfilesPublic}
								itemTitle={upp => upp.realName}
								itemSubtitle={upp => [upp.userName]}
								itemImageContent={upp => (upp.images && upp.images.length && upp.images) || upp.profileImage}
								itemIconName={null}
							/>
							<div className={styles.buttonLine}>
								<AddButton onClick={() => this.props.appRedirect('/addVerification')} className={styles.addButtonInExpandable}>{this.context.translate('Add verification')}</AddButton>
							</div>
						</ExpandableSection>


						<span ref={this.addRef(stepTypes.YOURFIT)} />

						<ExpandableSection title={this.context.translate('Stats')} defaultExpanded>
							<Collection
								items={
									up.statistics ?
										Object.keys(up.statistics).filter(key => !hasNoStatValue(up.statistics, key)).reduce((a, key) => ({ ...a, [key]: up.statistics[key] }), {}) :
										[]
								}
								contextMenuItems={() => [
									{
										name: this.context.translate('Edit'),
										handler: this.handleEditStat
									}
								]}
								classes={{ itemRoot: styles.itemOwnProfile }}
								renderItemFragment={({ item: stat, key, onContextClick }) => {
									const statName = statisticNameFor(key)
									const statUser = getField(this.props.user, 'userProfile.statistics', key) // viewing user's stat
									const statUnit = getField(statUser, 'unit') // display is to be in the viewing user's preferred unit
									const statValue = getValueInUnit(stat, statUnit)
									const statDisplay = statisticValueFor(statUnit).display(statValue)

									return (
										<React.Fragment>
											<div>{statName}</div>
											<div className={styles.itemFill} />
											<div>{statDisplay}</div>
											<IconButton className={styles.kebobMenu} aria-label="Context" onClick={onContextClick}>
												<MoreVertIcon />
											</IconButton>
										</React.Fragment>
									)
								}}
							/>
							<UserImproveBox userProfile={this.props.userProfile} appRedirect={this.props.appRedirect} />
						</ExpandableSection>

						<span ref={this.addRef(stepTypes.YOURKIT)} />

						{up.favourite && (
							<ExpandableSection title={this.context.translate('Owned items')} defaultExpanded>
								<Collection
									items={[up.favourite]}
									itemImageContent="/images/placeholders/basicjerseyplaceholder.jpg"
									itemImageClassName={styles.itemImage}
									itemTitle={f => `${f.brandName ? f.brandName : this.context.translate('Brand not specified')}${f.jerseyName ? ` - ${f.jerseyName}` : ''}`}
									itemSubtitle={f => `${f.size ? f.size.toUpperCase() : ''}${f.gender ? ` - ${genderDisplayFor(f.gender)}` : ''}`}
									itemIconName={null}
								/>
							</ExpandableSection>
						)}

					</div>
				)}
			</div>
		)
	}
}

User.propTypes = {
	appBottomBarValue: PropTypes.func.isRequired,
	appBackButtonFunction: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
	userProfile: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	verifiedUserObject: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	userProfilesPublic: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userProfileGetOwnProfile: PropTypes.func.isRequired
}

User.defaultProps = {
	userProfile: undefined,
	verifiedUserObject: undefined,
	userProfilesPublic: []
}

User.contextTypes = {
	translate: PropTypes.func
}

export const testing = {
	User
}

//	Redux
export default connect(
	/* istanbul ignore next line */
	state => pick(state, ['user', 'userProfile', 'verifiedUserObject', 'userProfilesPublic']),
	/* istanbul ignore next line */
	dispatch => mapDispatchers(dispatch, ['appRedirect', 'appBackButtonFunction', 'userProfileGetOwnProfile', 'appBottomBarValue'])
)(User)



// WEBPACK FOOTER //
// ./src/notActivated/User.jsx